/**
 * Checklist Service
 *
 * Handles checklists and checklist items for quality control,
 * safety inspections, and task verification.
 *
 * DB tables (migration 027):
 * - project_checklists: id, project_id, checklist_type, title, is_required,
 *   is_completed, completed_by, completed_at, created_at, updated_at
 * - project_checklist_items: id, checklist_id, description, sort_order,
 *   is_completed, completed_by, completed_at, photo_url, notes, created_at
 *
 * @module services/checklistService
 */

import { supabase } from './supabase';
import {
  STORAGE_BUCKETS,
  CHECKLIST_ITEM_STATUS,
} from '../utils/constants';

/**
 * Sentinel notes written by {@link markItemNotApplicable} / {@link skipChecklistItem}.
 *
 * `project_checklist_items` has NO `status` column — verified against
 * information_schema. The only completion state the table carries is
 * `is_completed` (boolean) plus a free-text `notes`. The UI's four-state model
 * (pending / completed / not_applicable / skipped) therefore has to be DERIVED
 * from those two columns; it can never be selected or filtered on. The screens
 * used to compare `item.status` against `CHECKLIST_ITEM_STATUS.*`, which is
 * always `undefined !== 'pending'` — so every bucket came out empty and the
 * detail page rendered zero items, always.
 */
export const ITEM_NOTE_NOT_APPLICABLE = 'No aplica';
export const ITEM_NOTE_SKIPPED = 'Omitido';

/**
 * Derive the UI status of a checklist item from the columns that actually exist.
 * @param {Object} item - project_checklist_items row
 * @returns {string} one of CHECKLIST_ITEM_STATUS
 */
export const deriveItemStatus = (item) => {
  if (!item?.is_completed) return CHECKLIST_ITEM_STATUS.PENDING;
  const notes = item.notes || '';
  if (notes.startsWith(ITEM_NOTE_NOT_APPLICABLE)) return CHECKLIST_ITEM_STATUS.NOT_APPLICABLE;
  if (notes.startsWith(ITEM_NOTE_SKIPPED)) return CHECKLIST_ITEM_STATUS.SKIPPED;
  return CHECKLIST_ITEM_STATUS.COMPLETED;
};

/** Decorate a single item row with its derived `status`. */
const withStatus = (item) => (item ? { ...item, status: deriveItemStatus(item) } : item);

/** Decorate an array of item rows with their derived `status`. */
const withStatuses = (items) => (items || []).map(withStatus);

/**
 * Get all checklists for a project
 * @param {string} projectId - Project ID
 * @param {Object} filters - Filter options
 * @param {string} filters.checklistType - Filter by checklist_type
 * @param {boolean} filters.completed - Filter by completion status
 * @returns {Promise<{data, error}>}
 */
export const getChecklists = async (projectId, filters = {}) => {
  try {
    let query = supabase
      .from('project_checklists')
      .select(`
        *,
        items:project_checklist_items(
          id,
          description,
          sort_order,
          is_completed,
          completed_at,
          notes
        )
      `)
      .eq('project_id', projectId);

    if (filters.checklistType) {
      query = query.eq('checklist_type', filters.checklistType);
    }

    if (filters.completed !== undefined) {
      query = query.eq('is_completed', filters.completed);
    }

    query = query.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) throw error;

    // Calculate progress for each checklist
    const checklistsWithProgress = data?.map(checklist => {
      const items = checklist.items || [];
      const completedItems = items.filter(i => i.is_completed).length;
      const totalItems = items.length;
      const progress = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

      return {
        ...checklist,
        items: withStatuses(items),
        progress,
        completedItems,
        totalItems,
      };
    });

    return { data: checklistsWithProgress, error: null };
  } catch (error) {
    console.error('[CHECKLIST] Get checklists error:', error);
    return { data: null, error };
  }
};

/**
 * Get checklist items for a specific checklist
 * @param {string} checklistId - Checklist ID
 * @returns {Promise<{data, error}>}
 */
export const getChecklistItems = async (checklistId) => {
  try {
    const { data, error } = await supabase
      .from('project_checklist_items')
      .select(`
        *,
        completed_by_profile:profiles!completed_by(id, full_name)
      `)
      .eq('checklist_id', checklistId)
      .order('sort_order', { ascending: true });

    if (error) throw error;

    return { data: withStatuses(data), error: null };
  } catch (error) {
    console.error('[CHECKLIST] Get items error:', error);
    return { data: null, error };
  }
};

/**
 * Get a single checklist with all items
 * @param {string} checklistId - Checklist ID
 * @returns {Promise<{data, error}>}
 */
export const getChecklist = async (checklistId) => {
  try {
    const { data, error } = await supabase
      .from('project_checklists')
      .select(`
        *,
        project:projects(id, project_number, title),
        completed_by_profile:profiles!completed_by(id, full_name),
        items:project_checklist_items(
          *,
          completed_by_profile:profiles!completed_by(id, full_name)
        )
      `)
      .eq('id', checklistId)
      .single();

    if (error) throw error;

    // Calculate progress
    const items = data.items || [];
    const completedItems = items.filter(i => i.is_completed).length;
    const totalItems = items.length;
    const progress = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

    return {
      data: {
        ...data,
        items: withStatuses(items),
        progress,
        completedItems,
        totalItems,
      },
      error: null,
    };
  } catch (error) {
    console.error('[CHECKLIST] Get checklist error:', error);
    return { data: null, error };
  }
};

/**
 * Complete a checklist item
 * @param {string} itemId - Checklist item ID
 * @param {Object} data - Completion data
 * @param {string} data.notes - Completion notes (optional)
 * @param {string} data.photo_url - Photo evidence URL (optional)
 * @returns {Promise<{data, checklistCompleted, error}>}
 */
export const completeChecklistItem = async (itemId, data = {}) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    // Update item
    const { data: item, error } = await supabase
      .from('project_checklist_items')
      .update({
        is_completed: true,
        completed_by: user.id,
        completed_at: new Date().toISOString(),
        photo_url: data.photo_url || null,
        notes: data.notes || null,
      })
      .eq('id', itemId)
      .select('*, checklist_id')
      .single();

    if (error) throw error;

    // Check if all items in checklist are completed
    const checklistId = item.checklist_id;
    const { data: allItems, error: itemsError } = await supabase
      .from('project_checklist_items')
      .select('is_completed')
      .eq('checklist_id', checklistId);

    if (itemsError) throw itemsError;

    const allCompleted = allItems.every(i => i.is_completed);

    // Auto-complete checklist if all items are done
    let checklistCompleted = false;
    if (allCompleted) {
      const { error: updateError } = await supabase
        .from('project_checklists')
        .update({
          is_completed: true,
          completed_by: user.id,
          completed_at: new Date().toISOString(),
        })
        .eq('id', checklistId);

      if (!updateError) {
        checklistCompleted = true;
      }
    }

    return { data: withStatus(item), checklistCompleted, error: null };
  } catch (error) {
    console.error('[CHECKLIST] Complete item error:', error);
    return { data: null, checklistCompleted: false, error };
  }
};

/**
 * Uncomplete a checklist item
 * @param {string} itemId - Checklist item ID
 * @returns {Promise<{data, error}>}
 */
export const uncompleteChecklistItem = async (itemId) => {
  try {
    const { data, error } = await supabase
      .from('project_checklist_items')
      .update({
        is_completed: false,
        completed_by: null,
        completed_at: null,
        photo_url: null,
        notes: null,
      })
      .eq('id', itemId)
      .select()
      .single();

    if (error) throw error;

    // Also uncomplete the checklist if it was marked as completed
    await supabase
      .from('project_checklists')
      .update({
        is_completed: false,
        completed_by: null,
        completed_at: null,
      })
      .eq('id', data.checklist_id);

    return { data: withStatus(data), error: null };
  } catch (error) {
    console.error('[CHECKLIST] Uncomplete item error:', error);
    return { data: null, error };
  }
};

/**
 * Mark a checklist item as not applicable
 * @param {string} itemId - Checklist item ID
 * @param {string} notes - Reason for N/A
 * @returns {Promise<{data, error}>}
 */
export const markItemNotApplicable = async (itemId, notes = '') => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { data, error } = await supabase
      .from('project_checklist_items')
      .update({
        is_completed: true,
        completed_by: user.id,
        completed_at: new Date().toISOString(),
        // The sentinel must lead so `deriveItemStatus` can recover the state;
        // any reason the user gave rides behind it.
        notes: notes ? `${ITEM_NOTE_NOT_APPLICABLE}: ${notes}` : ITEM_NOTE_NOT_APPLICABLE,
      })
      .eq('id', itemId)
      .select()
      .single();

    if (error) throw error;

    return { data: withStatus(data), error: null };
  } catch (error) {
    console.error('[CHECKLIST] Mark not applicable error:', error);
    return { data: null, error };
  }
};

/**
 * Skip a checklist item
 * @param {string} itemId - Checklist item ID
 * @param {string} notes - Reason for skipping
 * @returns {Promise<{data, error}>}
 */
export const skipChecklistItem = async (itemId, notes = '') => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { data, error } = await supabase
      .from('project_checklist_items')
      .update({
        is_completed: true,
        completed_by: user.id,
        completed_at: new Date().toISOString(),
        notes: notes ? `${ITEM_NOTE_SKIPPED}: ${notes}` : ITEM_NOTE_SKIPPED,
      })
      .eq('id', itemId)
      .select()
      .single();

    if (error) throw error;

    return { data: withStatus(data), error: null };
  } catch (error) {
    console.error('[CHECKLIST] Skip item error:', error);
    return { data: null, error };
  }
};

/**
 * Manually complete a checklist (mark as complete)
 * @param {string} checklistId - Checklist ID
 * @returns {Promise<{data, error}>}
 */
export const completeChecklist = async (checklistId) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    // Update checklist
    const { data, error } = await supabase
      .from('project_checklists')
      .update({
        is_completed: true,
        completed_by: user.id,
        completed_at: new Date().toISOString(),
      })
      .eq('id', checklistId)
      .select(`
        *,
        project:projects(id, project_number, title),
        items:project_checklist_items(id, description, is_completed)
      `)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[CHECKLIST] Complete checklist error:', error);
    return { data: null, error };
  }
};

// ============================================
// ADMIN FUNCTIONS
// ============================================

/**
 * Create a new checklist (admin)
 * @param {Object} checklistData - Checklist data
 * @param {string} checklistData.project_id - Project ID
 * @param {string} checklistData.title - Checklist title
 * @param {string} checklistData.checklist_type - Type (pre_work, safety, completion, quality, custom)
 * @param {boolean} checklistData.is_required - Whether completion is required
 * @param {Array} checklistData.items - Initial items (optional, array of strings or {description} objects)
 * @returns {Promise<{data, error}>}
 */
export const createChecklist = async (checklistData) => {
  try {
    if (!checklistData.project_id) throw new Error('Se requiere el proyecto');
    if (!checklistData.title) throw new Error('Se requiere el titulo');

    // Create checklist
    const { data: checklist, error } = await supabase
      .from('project_checklists')
      .insert({
        project_id: checklistData.project_id,
        title: checklistData.title,
        checklist_type: checklistData.checklist_type || 'custom',
        is_required: checklistData.is_required || false,
        is_completed: false,
      })
      .select()
      .single();

    if (error) throw error;

    // Add initial items if provided
    if (checklistData.items && checklistData.items.length > 0) {
      // `sort_order` explícito gana sobre el índice del array. Sin esto, el
      // checklist de cierre sembrado desde la web quedaba en 0..6 mientras el
      // carril MCP lo siembra en 1..7 con la MISMA definición compartida
      // (src/constants/closureChecklist.json): dos órdenes distintos para la
      // misma lista, que es justo la deriva que este cambio evita.
      const itemsToInsert = checklistData.items.map((item, index) => ({
        checklist_id: checklist.id,
        description: typeof item === 'string' ? item : (item.description || item.title || ''),
        sort_order:
          typeof item === 'object' && item !== null && item.sort_order !== undefined
            ? item.sort_order
            : index,
        is_completed: false,
      }));

      const { error: itemsError } = await supabase
        .from('project_checklist_items')
        .insert(itemsToInsert);

      if (itemsError) {
        console.error('Error adding items:', itemsError);
      }
    }

    // Fetch complete checklist with items
    return getChecklist(checklist.id);
  } catch (error) {
    console.error('[CHECKLIST] Create checklist error:', error);
    return { data: null, error };
  }
};

/**
 * Add an item to a checklist (admin)
 * @param {Object} itemData - Item data
 * @param {string} itemData.checklist_id - Checklist ID
 * @param {string} itemData.description - Item description
 * @param {number} itemData.sort_order - Sort order (optional)
 * @returns {Promise<{data, error}>}
 */
export const addChecklistItem = async (itemData) => {
  try {
    if (!itemData.checklist_id) throw new Error('Se requiere el checklist');
    if (!itemData.description) throw new Error('Se requiere la descripcion del item');

    // Get max sort order if not provided
    let sortOrder = itemData.sort_order;
    if (sortOrder === undefined) {
      const { data: existingItems, error: countError } = await supabase
        .from('project_checklist_items')
        .select('sort_order')
        .eq('checklist_id', itemData.checklist_id)
        .order('sort_order', { ascending: false })
        .limit(1);

      if (!countError && existingItems?.length > 0) {
        sortOrder = existingItems[0].sort_order + 1;
      } else {
        sortOrder = 0;
      }
    }

    const { data, error } = await supabase
      .from('project_checklist_items')
      .insert({
        checklist_id: itemData.checklist_id,
        description: itemData.description,
        sort_order: sortOrder,
        is_completed: false,
      })
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[CHECKLIST] Add item error:', error);
    return { data: null, error };
  }
};

/**
 * Update a checklist item (admin)
 * @param {string} itemId - Item ID
 * @param {Object} updates - Item updates (description, sort_order, notes, photo_url)
 * @returns {Promise<{data, error}>}
 */
export const updateChecklistItem = async (itemId, updates) => {
  try {
    const { data, error } = await supabase
      .from('project_checklist_items')
      .update(updates)
      .eq('id', itemId)
      .select()
      .single();

    if (error) throw error;

    return { data: withStatus(data), error: null };
  } catch (error) {
    console.error('[CHECKLIST] Update item error:', error);
    return { data: null, error };
  }
};

/**
 * Delete a checklist item (admin)
 * @param {string} itemId - Item ID
 * @returns {Promise<{success, error}>}
 */
export const deleteChecklistItem = async (itemId) => {
  try {
    const { error } = await supabase
      .from('project_checklist_items')
      .delete()
      .eq('id', itemId);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error('[CHECKLIST] Delete item error:', error);
    return { success: false, error };
  }
};

/**
 * Delete a checklist and all its items (admin)
 * @param {string} checklistId - Checklist ID
 * @returns {Promise<{success, error}>}
 */
export const deleteChecklist = async (checklistId) => {
  try {
    // Items will be cascade deleted by database
    const { error } = await supabase
      .from('project_checklists')
      .delete()
      .eq('id', checklistId);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error('[CHECKLIST] Delete checklist error:', error);
    return { success: false, error };
  }
};

/**
 * Upload photo for checklist item
 * @param {File} file - Image file
 * @param {string} checklistId - Checklist ID
 * @param {string} itemId - Item ID
 * @returns {Promise<{url, path, error}>}
 */
export const uploadChecklistItemPhoto = async (file, checklistId, itemId) => {
  try {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Tipo de archivo no permitido. Use JPG, PNG o WebP.');
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error('El archivo excede el tamano maximo de 10MB.');
    }

    const fileExt = file.name.split('.').pop();
    const fileName = `checklists/${checklistId}/${itemId}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKETS.PROGRESS_PHOTOS)
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) throw error;

    // Get signed URL
    const { data: urlData, error: urlError } = await supabase.storage
      .from(STORAGE_BUCKETS.PROGRESS_PHOTOS)
      .createSignedUrl(data.path, 3600); // 1-hour expiry (see CLAUDE.md storage policy)

    if (urlError) {
      const { data: { publicUrl } } = supabase.storage
        .from(STORAGE_BUCKETS.PROGRESS_PHOTOS)
        .getPublicUrl(data.path);
      return { url: publicUrl, path: data.path, error: null };
    }

    return { url: urlData.signedUrl, path: data.path, error: null };
  } catch (error) {
    console.error('[CHECKLIST] Upload photo error:', error);
    return { url: null, path: null, error };
  }
};

const checklistService = {
  getChecklists,
  getChecklistItems,
  getChecklist,
  completeChecklistItem,
  uncompleteChecklistItem,
  markItemNotApplicable,
  skipChecklistItem,
  completeChecklist,
  // Admin functions
  createChecklist,
  addChecklistItem,
  updateChecklistItem,
  deleteChecklistItem,
  deleteChecklist,
  uploadChecklistItemPhoto,
};

export default checklistService;
